import React, {useCallback, useEffect, useRef, useState} from "react"
import {createPortal} from "react-dom"

import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext"
import {useLexicalEditable} from "@lexical/react/useLexicalEditable"
import {$getSelection, $isRangeSelection, NodeKey} from "lexical"
import {
    $getTableNodeFromLexicalNodeOrThrow,
    $getTableRowIndexFromTableCellNode,
    $isSimpleTable,
    $isTableCellNode,
    TableCellHeaderStates,
    TableCellNode,
} from "@lexical/table"
import {$findMatchingParent} from "@lexical/utils"
import TableActionMenu from "./menu"
import {tableCellIsColumnHeader, tableCellIsMerged, tableCellIsRowHeader} from "./actions"
import {tableGetSortDirection, TableSortDirection} from "./sorting"

const debug = false
// const debug = "TableActionsPlugin"

type CellState = {
    key             : NodeKey
    isRowHeader     : boolean
    isColumnHeader  : boolean
    isMerged        : boolean
    canDeleteRow    : boolean
    canDeleteColumn : boolean
    canSort         : boolean
    sortDirection   : null | TableSortDirection
}

type Props = {
    anchorElem?: HTMLElement
}

export default function TableActionsPlugin(props: Props) {
    const [editor]              = useLexicalComposerContext()
    const isEditable            = useLexicalEditable()
    const [cell, setCell]       = useState<null | CellState>(null)
    const [position, setPosition] = useState({left: 0, top: 0})
    const [showMenu, setShowMenu] = useState(false)
    const buttonRef             = useRef<HTMLButtonElement | null>(null)

    // Reads the cell containing the selection, plus whether its row/column may be removed
    const readSelectedCell = useCallback(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) {
            return null
        }

        const cellNode = $findMatchingParent(selection.anchor.getNode(), $isTableCellNode)
        if (!$isTableCellNode(cellNode)) {
            return null
        }

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(cellNode)
        const cell      = cellNode as TableCellNode

        // Offered on top-row header cells only, as the legacy menu did. Column index ignores
        // colspans, so merged tables are excluded.
        const canSort = $getTableRowIndexFromTableCellNode(cell) === 0
            && cell.hasHeaderState(TableCellHeaderStates.ROW)
            && tableNode.getChildrenSize() > 2
            && $isSimpleTable(tableNode)

        return {
            key             : cellNode.getKey(),
            isRowHeader     : tableCellIsRowHeader(cell),
            isColumnHeader  : tableCellIsColumnHeader(cell),
            isMerged        : tableCellIsMerged(cell),
            canDeleteRow    : tableNode.getChildrenSize() > 1,
            canDeleteColumn : tableNode.getColumnCount() > 1,
            canSort,
            sortDirection   : canSort ? tableGetSortDirection(cell) : null,
        }
    }, [])

    const updatePosition = useCallback((cellKey: NodeKey) => {
        const anchorElem = props.anchorElem
        const cellElem   = editor.getElementByKey(cellKey)
        if (!anchorElem || !cellElem) {
            return
        }

        const cellRect      = cellElem.getBoundingClientRect()
        const anchorRect    = anchorElem.getBoundingClientRect()

        setPosition({
            left    : cellRect.right - anchorRect.left - 22,
            top     : cellRect.top - anchorRect.top + 4,
        })
    }, [editor, props.anchorElem])

    useEffect(() => {
        if (!isEditable) {
            setCell(null)
            return
        }

        return editor.registerUpdateListener(({editorState}) => {
            const next = editorState.read(readSelectedCell)

            debug && console.log(debug, 'selected cell', next)

            setCell(prev => {
                // avoid re-rendering (and closing the menu) while typing in the same cell
                return _.isEqual(prev, next) ? prev : next
            })

            if (next) {
                updatePosition(next.key)
            }
        })
    }, [editor, isEditable, readSelectedCell, updatePosition])

    // the cell can move without the selection changing - e.g. a column resize or a sibling edit
    useEffect(() => {
        if (!cell) {
            setShowMenu(false)
            return
        }
        updatePosition(cell.key)
    }, [cell, updatePosition])

    if (!isEditable || !cell || !props.anchorElem) {
        return null
    }

    return (
        createPortal(
            <>
                <button ref         = {buttonRef}
                        className   = "editor-tableCellActionButton"
                        style       = {{position: 'absolute', left: position.left, top: position.top, zIndex: 4}}
                        onClick     = {(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setShowMenu(!showMenu)
                        }}>
                    <div className="menu-icon i5-big-angle-down"/>
                </button>

                {showMenu &&
                    <TableActionMenu
                        editor          = {editor}
                        cellKey         = {cell.key}
                        isRowHeader     = {cell.isRowHeader}
                        isColumnHeader  = {cell.isColumnHeader}
                        isMerged        = {cell.isMerged}
                        canDeleteRow    = {cell.canDeleteRow}
                        canDeleteColumn = {cell.canDeleteColumn}
                        canSort         = {cell.canSort}
                        sortDirection   = {cell.sortDirection}
                        getTargetFunc   = {() => buttonRef.current}
                        onClose         = {() => setShowMenu(false)}
                    />
                }
            </>,
            props.anchorElem,
        )
    )
}
